import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';

import { Alert, Badge, Button, Card, Form, Modal, Spinner } from 'react-bootstrap';
import classNames from 'classnames';
import { faEdit, faComments, faSave, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { AuthContext } from 'components/providers/AuthContextProvider';
import {
  GLOBAL_VENDOR_COMMENTS_GET,
  GLOBAL_VENDOR_COMMENTS_CREATE,
  GLOBAL_VENDOR_COMMENTS_UPDATE,
  GLOBAL_VENDOR_COMMENTS_DELETE,
  getURL,
} from 'lib/networking/endpoints';
import { getAge } from 'lib/utils/dateUtils';
import pointerOnHover from 'lib/css/pointerOnHover';
import SwalDialog, { areYouSure } from 'lib/utils/toast';
import useDelete from 'lib/hooks/useDelete';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';
import usePut from 'lib/hooks/usePut';

function VendorCommentModal({ show, onHide, globalVendorId, vendorName, onCommentUpdated }) {
  const { authUser } = useContext(AuthContext);
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [error, setError] = useState('');
  const [updatingCommentId, setUpdatingCommentId] = useState(null);

  const {
    data: { data: comments = [] } = {},
    loading,
    refetch: refetchComments,
  } = useGet(
    getURL(GLOBAL_VENDOR_COMMENTS_GET, { global_vendor_id: globalVendorId }),
    {},
    !show || !globalVendorId,
  );

  const { postData: createComment, loading: creatingComment } = usePost(
    GLOBAL_VENDOR_COMMENTS_CREATE,
    () => {
      setNewComment('');
      setError('');
      refetchComments();
      onCommentUpdated?.();
    },
    () => {
      SwalDialog('error', 'Error occurred while Creating Comment', 'Error', 'center');
    },
  );

  const { putData: updateComment } = usePut(
    GLOBAL_VENDOR_COMMENTS_UPDATE,
    () => {
      setEditingCommentId(null);
      setEditingText('');
      setUpdatingCommentId(null);
      refetchComments();
      onCommentUpdated?.();
    },
    () => {
      SwalDialog('error', 'Error occurred while Updating Comment', 'Error', 'center');
    },
  );

  const { deleteData: deleteComment } = useDelete(GLOBAL_VENDOR_COMMENTS_DELETE, false, () => {
    refetchComments();
    onCommentUpdated?.();
  });

  const submitting = creatingComment || !!updatingCommentId;

  const handleCreateComment = () => {
    if (!newComment.trim()) return;
    setError('');
    createComment({
      global_vendor_id: globalVendorId,
      comment: newComment.trim(),
    });
  };

  const handleDeleteComment = commentId => {
    areYouSure(() => {
      deleteComment({ comment_id: commentId });
    }, 'Are you sure you want to delete this comment?');
  };

  const startEdit = comment => {
    setEditingCommentId(comment.id);
    setEditingText(comment.comment);
  };

  const cancelEdit = () => {
    setEditingCommentId(null);
    setEditingText('');
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faComments} className="mr-2" />
          Comments for {vendorName || 'Vendor'}
          {comments.length > 0 && (
            <Badge variant="secondary" className="ml-2">
              {comments.length}
            </Badge>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Add New Comment Section */}
        <Card className="mb-3">
          <Card.Body>
            <Form.Group>
              <Form.Label className="font-weight-bold">
                <FontAwesomeIcon icon={faEdit} className="mr-2" />
                Add New Comment
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                placeholder="Share your thoughts about this vendor..."
                maxLength={512}
              />
              <Form.Text className="text-muted">{newComment.length}/512 characters</Form.Text>
            </Form.Group>
            <div className="d-flex justify-content-end mt-2">
              <Button
                variant="primary"
                onClick={handleCreateComment}
                disabled={!newComment.trim() || submitting}
              >
                Add Comment
              </Button>
            </div>
          </Card.Body>
        </Card>

        {/* Comments List */}
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <div className="mt-2">Loading comments...</div>
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-4 text-muted">
            <FontAwesomeIcon icon={faComments} size="3x" className="mb-3 text-secondary" />
            <div>No comments yet. Be the first to add one!</div>
          </div>
        ) : (
          comments.map(comment => (
            <Card key={comment.id} className="mb-3">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <strong>{comment.created_by?.name || 'Unknown User'}</strong>
                    <span className="text-muted ml-2">{getAge(comment.created_at)}</span>
                  </div>
                  {comment.created_by?.id === authUser?.id && (
                    <div className="d-flex">
                      {editingCommentId === comment.id ? (
                        <>
                          <Button
                            variant="success"
                            size="sm"
                            className="mr-1"
                            onClick={() => {
                              setUpdatingCommentId(comment.id);
                              updateComment({
                                comment_id: comment.id,
                                comment: editingText.trim(),
                              });
                            }}
                            disabled={!editingText.trim() || updatingCommentId === comment.id}
                          >
                            {updatingCommentId === comment.id ? (
                              <Spinner size="sm" />
                            ) : (
                              <FontAwesomeIcon icon={faSave} />
                            )}
                          </Button>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={cancelEdit}
                            disabled={updatingCommentId === comment.id}
                          >
                            <FontAwesomeIcon icon={faTimes} />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className={classNames('mr-1', pointerOnHover)}
                            onClick={() => startEdit(comment)}
                          >
                            <FontAwesomeIcon icon={faEdit} />
                          </Button>
                          <Button
                            variant="outline-danger"
                            size="sm"
                            className={pointerOnHover}
                            onClick={() => handleDeleteComment(comment.id)}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>

                {editingCommentId === comment.id ? (
                  <Form.Group className="mb-0">
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={editingText}
                      onChange={e => setEditingText(e.target.value)}
                      disabled={updatingCommentId === comment.id}
                      maxLength={512}
                    />
                    <Form.Text className="text-muted">
                      {editingText.length}/512 characters
                    </Form.Text>
                  </Form.Group>
                ) : (
                  <div style={{ whiteSpace: 'pre-wrap' }}>{comment.comment}</div>
                )}
              </Card.Body>
            </Card>
          ))
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

VendorCommentModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  globalVendorId: PropTypes.number,
  vendorName: PropTypes.string,
  onCommentUpdated: PropTypes.func,
};

export default VendorCommentModal;
